/**
 * Dial-tone audio pulse using Web Audio. Replaces `dialing-tone.js`.
 *
 * Fixes vs. legacy:
 *   - Single-play guard (idempotent `start()`)
 *   - Proper gain ramping (no click on start/stop)
 *   - Always tears down nodes on stop — no oscillator accumulation
 *   - 425 Hz still hardcoded (Israeli dial tone standard); pulse 1s on / 3s off
 */

import { createLogger } from '../../core/logger.js';

const log = createLogger('dialing-tone');

const TONE_HZ = 425;
const PULSE_ON_MS = 1000;
const PULSE_OFF_MS = 3000;
// Peak gain — Web Audio is unitless, 0..1 with 1.0 being clip-level.
// The speaker toggle on the calling screen flips between NORMAL and BOOST.
// BOOST sits just under clip; NORMAL is a comfortable indoor level.
// Earlier static value of 0.18 was inaudible on iOS speakers next to ambient noise.
const GAIN_NORMAL = 0.6;
const GAIN_BOOST = 0.95;
let currentPeak = GAIN_NORMAL;
const GAIN_RAMP_MS = 40;

/** @type {AudioContext | null} */
let ctx = null;
/** @type {OscillatorNode | null} */
let osc = null;
/** @type {GainNode | null} */
let gain = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let pulseTimer = null;
let playing = false;

/**
 * Start the dial-tone pulse. Idempotent.
 *
 * The returned promise resolves once the oscillator is actually producing
 * audible audio (i.e. AudioContext has resumed if it was suspended). Some
 * WebView contexts swallow the first tone because the oscillator was
 * `start()`-ed while the AudioContext was still in `suspended` state —
 * waiting for the resume promise fixes that.
 *
 * @returns {Promise<boolean>} true if started (or already running), false if AudioContext unavailable
 */
export async function start() {
  if (playing) return true;
  try {
    const AC = /** @type {any} */ (window).AudioContext || /** @type {any} */ (window).webkitAudioContext;
    if (!AC) return false;
    if (!ctx) ctx = new AC();
    // iOS Safari 16.4+: by default Web Audio is in the "ambient" audio
    // category and gets silenced by the hardware mute switch. Setting the
    // session type to "playback" routes it like a real phone call — audible
    // even when the ringer switch is off. Safe no-op on Android / older iOS.
    try {
      const navAny = /** @type {any} */ (navigator);
      if (navAny.audioSession && navAny.audioSession.type !== 'playback') {
        navAny.audioSession.type = 'playback';
      }
    } catch { /* ignore — unsupported */ }
    const c = /** @type {AudioContext} */ (ctx);
    if (c.state === 'suspended') {
      try { await c.resume(); }
      catch (e) { log.warn('resume:', /** @type {Error} */ (e).message); }
    }
    osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = TONE_HZ;
    gain = c.createGain();
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start();
    playing = true;
    schedulePulse(true);
    return true;
  } catch (err) {
    log.warn('start failed:', err);
    cleanup();
    return false;
  }
}

/** Stop and tear down. Idempotent. */
export function stop() {
  if (!playing && !osc) return;
  playing = false;
  if (pulseTimer) { clearTimeout(pulseTimer); pulseTimer = null; }
  cleanup();
}

/** @returns {boolean} */
export function isPlaying() {
  return playing;
}

/**
 * Toggle "speaker" mode — raises (or lowers) the peak gain live. If a pulse
 * is currently in the ON phase, the gain is rescheduled immediately so the
 * change is audible without waiting for the next cycle.
 *
 * @param {boolean} on
 */
export function setBoost(on) {
  currentPeak = on ? GAIN_BOOST : GAIN_NORMAL;
  // If we're mid-pulse, retarget the current ramp so the user hears the
  // change right away. We can't tell if we're in the ON or OFF half without
  // tracking it explicitly, but the gain node's *current* value tells us
  // enough: if it's non-zero we're audibly on and should retarget upward;
  // if zero, the next ON-phase will pick up the new value automatically.
  if (!playing || !gain || !ctx) return;
  const c = /** @type {AudioContext} */ (ctx);
  if (gain.gain.value > 0.01) {
    const now = c.currentTime;
    const ramp = GAIN_RAMP_MS / 1000;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(currentPeak, now + ramp);
    } catch { /* ignore */ }
  }
}

/** @returns {boolean} */
export function isBoosted() {
  return currentPeak === GAIN_BOOST;
}

// ====================================================================
// internals
// ====================================================================

/**
 * Schedule the next on/off transition. Reschedules itself indefinitely until
 * `stop()` is called.
 * @param {boolean} on
 */
function schedulePulse(on) {
  if (!playing || !gain || !ctx) return;
  const now = ctx.currentTime;
  const ramp = GAIN_RAMP_MS / 1000;
  const target = on ? currentPeak : 0;
  try {
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(target, now + ramp);
  } catch (err) {
    log.warn('gain ramp failed:', err);
  }
  pulseTimer = setTimeout(() => schedulePulse(!on), on ? PULSE_ON_MS : PULSE_OFF_MS);
}

function cleanup() {
  try { osc?.stop(); } catch { /* ignore */ }
  try { osc?.disconnect(); } catch { /* ignore */ }
  try { gain?.disconnect(); } catch { /* ignore */ }
  osc = null;
  gain = null;
  // Keep AudioContext alive — Safari penalises closing/reopening on iOS.
}
